import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { FamilyProvider, useFamilyContext } from './contexts/FamilyContext';
import { MalcolmProvider } from './contexts/MalcolmContext';
import { SessionProvider } from './contexts/SessionContext';
import { SharingProvider } from './contexts/SharingContext';
import SprintTimerOverlay from './components/College/SprintTimerOverlay';
import Auth from './components/Auth';
import './lib/notification-scheduler'; // Initialize SMS schedulers
import './styles/shared-components.css'; // Shared component styles
import './styles/neo-brutalist-colleges.css'; // Neo-brutalist college card styles
import { initializePerformanceOptimizations } from './utils/architecture/performanceOptimization.jsx';
import { initializeEventSystem } from './services/events/CollegeServiceIntegration';
import InstallPrompt from './components/PWA/InstallPrompt';
import PWAStatus from './components/PWA/PWAStatus';
import UpdateNotification from './components/PWA/UpdateNotification';
import SplashScreen from './components/PWA/SplashScreen';
import ErrorBoundary from './components/ErrorBoundary';
import pwaAuthFix from './utils/pwaAuthFix';
import FamilySetup from './components/Authentication/FamilySetup';
import ResetPassword from './components/Authentication/ResetPassword';
import AuthCallback from './components/Authentication/AuthCallback';
import DashboardContainer from './components/Dashboard/DashboardContainer';
import AdminDashboard from './components/Admin/AdminDashboard';
import SchemaExplorer from './components/Admin/SchemaExplorer';
import AdminQueue from './pages/AdminQueue';
import AdminScraper from './pages/AdminScraper';
import TaskIntegrityDashboard from './components/Admin/TaskIntegrityDashboard';
import RecommendationWorkflowGenerator from './components/Admin/RecommendationWorkflowGenerator';
import PlanningInterface from './components/Planning/PlanningInterface';
import DailyCheckIn from './components/Tracking/DailyCheckIn';
import ActivityLibrary from './components/Libraries/ActivityLibrary';
import EnhancedPlanningHub from './components/Planning/EnhancedPlanningHub';

import UnifiedEventsPageDesktop from './components/Family/UnifiedEventsPageDesktop';
import FamilyEventsWithTabs from './components/Family/FamilyEventsWithTabs';

import SimplifiedCalendar from './components/Family/SimplifiedCalendar';
import RecsIdeas from './components/Ideas/RecsIdeas';
import WatchLibrary from './components/Ideas/WatchLibrary';
import ParentResponseDashboard from './components/Ideas/ParentResponseDashboard';
import PlanningHub from './components/Planning/PlanningHub';

import SMSSettings from './components/Settings/SMSSettings';
import SMSTestComponent from './components/Settings/SMSTestComponent';
import ProfileManagement from './components/Settings/ProfileManagement';
import AccountSettings from './components/Settings/AccountSettings';
import JoinFamily from './components/Authentication/JoinFamily';
import FamilyInvitations from './components/Family/FamilyInvitations';
import IncomePage from './components/Allowance/IncomePage';
import ShoppingDashboard from './components/Shopping/ShoppingDashboard';
import ShoppingHistory from './components/Shopping/ShoppingHistory';
import ShoppingLists from './components/Shopping/ShoppingLists';
import FrictionlessFamilyMessages from './components/Messages/FrictionlessFamilyMessages';

import PackingDashboard from './components/Packing/PackingDashboard';
import PackingListDetailEnhanced from './components/Packing/PackingListDetailEnhanced';

import TasksPage from './components/Tasks/TasksPage';
import ManagePage from './components/Manage/ManagePage';

// Emergency Recovery
import AssessmentRecoveryPage from './components/Emergency/AssessmentRecoveryPage';
import ActivityBrowsePage from './components/Family/ActivityBrowsePage';
import CollegePrep from './components/CollegePrep/CollegePrep';
import CollegeJourneyHubV2 from './components/College/CollegeJourneyHubV2';
// import CollegeJourneyHub from './components/College/CollegeJourneyHub'; // Archived 2025-10 - replaced by HubV2
// import CollegeJourneyHubEnhanced from './components/College/CollegeJourneyHub-Enhanced'; // Archived 2025-01

// Import mockups for review
import CollegeJourneyHubMockup from './mockups/CollegeJourneyHub-Contextual-Redesign';
import ContextScenariosDemo from './mockups/CollegeJourneyHub-Context-Scenarios';
import CollegeJourneyHubEnhancedMockup from './mockups/CollegeJourneyHub-Enhanced';
import CollegeRouteHandler from './components/College/CollegeRouteHandler';
import CollegeRouter from './components/College/CollegeRouter';
import GlobalCollegeViewWrapper from './components/College/GlobalCollegeViewWrapper';
import CollegeJourneyWrapper from './components/College/CollegeJourneyWrapper';
import EssayDevelopmentHub from './components/College/EssayDevelopmentHub/EssayDevelopmentHub';
import UnifiedEssayHub from './components/College/EssayDevelopmentHub/UnifiedEssayHub';
import CollegeComparison from './components/College/CollegeComparison';
import CollegeRecommendations from './components/College/CollegeRecommendations';
import BragSheetFramework from './components/College/BragSheetFramework';
import EnhancedBragSheetMockup from './components/College/EnhancedBragSheetMockup';
import FollowUpNotePage from './components/College/FollowUpNotePage';
import CollegeStrategyMockup2 from './components/Mockups/College-Strategy-v2-Mock';
import EssayAreaMocks from './mockups/EssaysAreaMocks';
import AssessmentAnalysisRefined from './components/Mockups/AssessmentAnalysisMock';
import CollegeJourneyRefined from './components/Mockups/HubMock';
import EssayIdeasHybrid from './components/Mockups/EssayModule1';
import EssayStartProgressive from './components/Mockups/EssayModule2';
import EssayResultsDisplay from './components/Mockups/EssayModule3';
import EssayTopicDevelopment from './components/Mockups/EssayModule4';
import EssayTopicsRedesign from './components/Mockups/EssayModule5';
import CJWrapperNav from './components/Mockups/CJ-Wrapper-Nav';

// import MasterTimeline from './components/College/MasterTimeline'; // Archived 2025-01 (replaced by UnifiedTimelineIntegrated)
import StudentAcademicProfile from './components/College/StudentAcademicProfile';
import FinancialAidHub from './components/College/FinancialAidHub';
import ConversationalAssessment from './components/College/ConversationalAssessment';
import ConversationalResults from './components/College/ConversationalResults';
import AssessmentEntry from './components/College/AssessmentEntry';
// import MigrationUtility from './components/College/MigrationUtility';
// import StatusChecker from './components/College/StatusChecker';
// import FixFamilyMembers from './components/College/FixFamilyMembers';
// import FamilyDiagnostics from './components/College/FamilyDiagnostics';
import ParentCollegeHub from './components/College/ParentCollegeHub';
import ParentTestingHub from './components/College/ParentTestingHub';
import TestProfileWizard from './components/College/TestProfileWizard';
// import EnhancedCollegeDataEntry from './components/College/EnhancedCollegeDataEntry'; // Archived 2025-01
import EnhancedCollegeStrategy from './components/College/EnhancedCollegeStrategy';
import CollegeStrategyV2 from './components/College/CollegeStrategyV2';
import CollegeStrategy from './components/College/CollegeStrategy';
import EnhancedCollegeDataHub from './components/College/EnhancedCollegeDataHub';
import CollegeProfile from './components/College/CollegeProfile';
import { CollegeProvider } from './contexts/CollegeContext';
import ReadingProvider from './contexts/ReadingContext';
import TestingModeIndicator from './components/Testing/TestingModeIndicator';
import StellaReadingDashboard from './components/Reading/StellaReadingDashboard';
import AIIntelligenceFeed from './components/Malcolm/AIIntelligenceFeed';
import ProjectSetupFlow from './components/Mockups/Project-Mock';
import CollegeStrategyMockup from './components/Mockups/2025.07.04 College Strat Mock';
import StudentOnboardingFlow from './components/College/StudentOnboardingFlow';
import ParentObservations from './components/College/ParentObservations';
import ParentObservationsB from './components/College/ParentObservationsB';
import StudentContextGenerator from './components/College/StudentContextGenerator';
import QuestionSetGenerator from './components/College/QuestionSetGenerator';
import DynamicGuidanceGenerator from './components/College/DynamicGuidanceGenerator';
import SynthesisInstructionsGenerator from './components/College/SynthesisInstructionsGenerator';
import FormattingCustomizer from './components/College/FormattingCustomizer';
// import UnifiedCollegeParentHub from './components/College/UnifiedCollegeParentHub'; // Archived 2025-01
import ComprehensiveStudentProfile from './components/College/ComprehensiveStudentProfile';
import ParentAssessmentResults from './components/College/ParentAssessmentResults';
import ClaudeApiLogs from './components/College/ClaudeApiLogs';
import SavedStrategicProfile from './components/College/SavedStrategicProfile';
import EssayRefinementInterface from './components/College/EssayRefinementInterface';
import AssessmentAnalysisPage from './components/College/AssessmentAnalysisPage';
import AgentsDashboard from './components/Agents/AgentsDashboard';
import StudentControlPanel from './components/Student/StudentControlPanel';
import TimelineProgressWidget from './components/Student/TimelineProgressWidget';
import OrganizedTimeline from './pages/OrganizedTimeline';
import WorkflowTimelinePage from './pages/WorkflowTimelinePage';
import TimelineDevPage from './pages/TimelineDevPage';
import UnifiedTimelineIntegrated from './components/College/_archived/UnifiedTimelineIntegrated';

// React Staging
import ReactStaging1 from './components/Mockups/ReactStaging1';
import ReactStaging2 from './components/Mockups/ReactStaging2';

// Showcase (no auth required)
import CollegeJourneyShowcase from './components/Showcase/CollegeJourneyShowcase';
import CollegeJourneyShowcaseAspirational from './components/ShowcaseAspirational/CollegeJourneyShowcaseAspirational';

// Sharing System
import SharingDemo from './components/Sharing/SharingDemo';
import StudentSharingPanel from './components/Sharing/StudentSharingPanel';
import StudentSharingDashboard from './components/Sharing/StudentSharingDashboard';
import ParentPortal from './components/Sharing/ParentPortal';
import RealParentPortal from './components/Sharing/RealParentPortal';

// Unified Parent Portal
import UnifiedParentPortal from './components/College/ParentPortal/UnifiedParentPortal';

import TabNav from './components/Shared/TabNav';
import SidebarLayout from './components/Layout/SidebarLayout';
import ScrollToTop from './components/Shared/ScrollToTop';
import Welcome from './components/Landing/Welcome';
import AdminReadingChallenges from './components/Reading/AdminReadingChallenges';
import IntelligenceDashboard from './components/Intelligence/IntelligenceDashboard';
import IntelligenceDashboardV2 from './components/Intelligence/IntelligenceDashboardV2';
import AdvancedAnalyticsDashboard from './components/Intelligence/AdvancedAnalyticsDashboard';




// Initialize performance optimizations
if (typeof window !== 'undefined') {
  initializePerformanceOptimizations();
}

function AppRoutes() {
  const { user, family, currentUser, loading, authInitialized } = useFamilyContext();

  // Initialize Task Integrity Event System on app load
  useEffect(() => {
    initializeEventSystem()
      .then(() => console.log('✅ Task Integrity Event System Active'))
      .catch(err => console.error('❌ Event System Init Failed:', err));
  }, []);

  // Debug logging for loading states
  console.log('🔍 App.jsx Loading Debug:', {
    loading,
    authInitialized, 
    user: !!user,
    family: !!family,
    currentUser: !!currentUser,
    isPWA: pwaAuthFix.isPWAMode()
  });

  // In PWA mode, skip the loading screen since splash already showed
  // For web mode, show loading state as normal
  if (loading && !pwaAuthFix.isPWAMode()) {
    console.log('⏳ App.jsx: Showing loading spinner because loading=true (web mode)');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading only if we're still initializing AND have no user/family data
  // Skip in PWA mode since splash already handled it
  if (!authInitialized && !user && !family && !pwaAuthFix.isPWAMode()) {
    console.log('⏳ App.jsx: Showing loading spinner because not authInitialized AND no user/family (web mode)');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Showcase routes - accessible without authentication
  if (window.location.pathname === '/showcase' || window.location.pathname === '/showcase-aspirational') {
    return (
      <Routes>
        <Route path="/showcase" element={<CollegeJourneyShowcase />} />
        <Route path="/showcase-aspirational" element={<CollegeJourneyShowcaseAspirational />} />
      </Routes>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/showcase" element={<CollegeJourneyShowcase />} />
        <Route path="/join-family" element={<JoinFamily />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<Auth />} />
      </Routes>
    );
  }

  // Debug logging
  console.log('🔍 App.jsx Routing Debug:', {
    user: user?.id,
    family: family?.id,
    familyExists: !!family,
    currentUser: currentUser?.id,
    currentUserExists: !!currentUser,
    authInitialized,
    loading
  });

  // Check if current user is a parent (including when viewing kids' dashboards)
  // If no currentUser is set yet, check if the logged-in user is a parent
  let isActualParent = currentUser?.isParent === true || currentUser?.originalParent === true;
  
  // If currentUser isn't set yet, try to determine from family data
  if (!currentUser && family && user) {
    const parent = family.parents?.find(p => p.authUserId === user.id);
    if (parent) {
      isActualParent = true;
      console.log('🔍 Detected parent user without currentUser set');
    }
  }

  // If we have no family at all AND auth is still loading, show limited routes
  // But if auth is complete and we just don't have family data, show full app
  if (!family && !authInitialized) {
    console.log('🔍 App.jsx: No family and auth not initialized, showing limited routes');
    return (
      <SidebarLayout>
        <Routes>
          <Route path="/" element={<Welcome />} />
          <Route path="/family-setup" element={<FamilySetup />} />
          <Route path="/parent-dashboard" element={<DashboardContainer />} />
          <Route path="/dashboard" element={<DashboardContainer />} />
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </SidebarLayout>
    );
  }
  
  // If auth is complete but no family, still show full app (family data might load later)
  if (!family && authInitialized) {
    console.log('🔍 App.jsx: Auth complete but no family, showing full app anyway');
  }

  return (
    <SidebarLayout>
      <ScrollToTop />
      <Routes>
        {/* Root route with proper welcome page */}
        <Route path="/" element={<Welcome />} />
        
        {/* React Staging */}
        <Route path="/mockup1" element={<ReactStaging1 />} />
        <Route path="/mockup2" element={<ReactStaging2 />} />
      
      {/* Parent-only routes */}
      <Route path="/parent-dashboard" element={
        isActualParent ? <DashboardContainer /> : <Navigate to="/dashboard" />
      } />
      <Route path="/family-invitations" element={
        isActualParent ? <FamilyInvitations /> : <Navigate to="/dashboard" />
      } />
      <Route path="/profile-management" element={
        isActualParent ? <ProfileManagement /> : <Navigate to="/dashboard" />
      } />
      <Route path="/account-settings" element={
        <AccountSettings /> 
      } />
            <Route path="/sms-settings" element={
        isActualParent ? <SMSSettings /> : <Navigate to="/dashboard" />
      } />
      <Route path="/sms-test" element={
        isActualParent ? <SMSTestComponent /> : <Navigate to="/dashboard" />
      } />
      <Route path="/reading/admin" element={
        (isActualParent || currentUser?.originalParent) ? 
          <ReadingProvider><AdminReadingChallenges /></ReadingProvider> : 
          <Navigate to="/dashboard" />
      } />
      <Route path="/college/dashboard" element={
        isActualParent ? <UnifiedParentPortal /> : <Navigate to="/college/dashboard" />
      } />
      <Route path="/admin" element={
        (isActualParent || currentUser?.originalParent) ?
          <AdminDashboard /> :
          <Navigate to="/dashboard" />
      } />
      <Route path="/admin/schema" element={
        (isActualParent || currentUser?.originalParent) ?
          <SchemaExplorer /> :
          <Navigate to="/dashboard" />
      } />
      <Route path="/admin/queue" element={
        (isActualParent || currentUser?.originalParent) ?
          <AdminQueue /> :
          <Navigate to="/dashboard" />
      } />
      <Route path="/admin/scraper" element={
        (isActualParent || currentUser?.originalParent) ?
          <AdminScraper /> :
          <Navigate to="/dashboard" />
      } />
      <Route path="/admin/integrity" element={
        (isActualParent || currentUser?.originalParent) ?
          <TaskIntegrityDashboard /> :
          <Navigate to="/dashboard" />
      } />
      <Route path="/admin/recommendation-generator" element={
        (isActualParent || currentUser?.originalParent) ?
          <RecommendationWorkflowGenerator /> :
          <Navigate to="/dashboard" />
      } />

      {/* Emergency Recovery - Parent only */}
      <Route path="/emergency/assessment-recovery" element={
        isActualParent ? <AssessmentRecoveryPage /> : <Navigate to="/dashboard" />
      } />

      {/* Unified dashboard for all users */}
      <Route path="/dashboard" element={<DashboardContainer />} />
      
      {/* Unified planning routes - accessible to both parents and kids */}
      <Route path="/plan" element={<EnhancedPlanningHub />} />
      
      {/* Activity Library - accessible to both parents and kids via Tasks page */}
      <Route path="/activities" element={
        <Navigate to="/tasks?tab=activities" />
      } />
      <Route path="/today" element={
        (!isActualParent || currentUser?.originalParent) ? 
          <DailyCheckIn /> : 
          <Navigate to="/dashboard" />
      } />
      
      {/* Parent-only planning routes */}
      <Route path="/plan" element={<EnhancedPlanningHub />} />
      
      <Route path="/recs-ideas" element={
        <ReadingProvider><RecsIdeas /></ReadingProvider>
      } />
      <Route path="/recs-ideas/watch" element={
        <ReadingProvider><WatchLibrary /></ReadingProvider>
      } />
      <Route path="/recs-ideas/responses" element={<ParentResponseDashboard />} />
      
      {/* Family setup - accessible when no family exists */}
      <Route path="/family-setup" element={<FamilySetup />} />
      
      {/* Shared routes - accessible by everyone */}
      
      <Route path="/family" element={<ActivityBrowsePage />} />
      <Route path="/calendar" element={<SimplifiedCalendar />} />
      {/* Redirect old calendar route to new simplified calendar */}
      <Route path="/family-calendar" element={<Navigate to="/calendar" />} />
      {/* Redirect old family-time route to unified view */}
      <Route path="/family-time" element={<Navigate to="/family?view=invites" />} />
      <Route path="/income-hub" element={<IncomePage />} />
      <Route path="/allowance" element={<IncomePage />} />
      <Route path="/shopping" element={<ShoppingDashboard />} />
      <Route path="/shopping/lists" element={<ShoppingLists />} />
      <Route path="/shopping/history" element={<ShoppingHistory />} />
      <Route path="/packing" element={<PackingDashboard />} />
      <Route path="/packing/:id" element={<PackingListDetailEnhanced />} />
      <Route path="/messages" element={<FrictionlessFamilyMessages />} />
      <Route path="/tasks" element={<TasksPage />} />
      <Route path="/manage" element={<ManagePage />} />


      <Route path="/recs-ideas" element={
        <ReadingProvider><RecsIdeas /></ReadingProvider>
      } />
      <Route path="/recs-ideas/watch" element={
        <ReadingProvider><WatchLibrary /></ReadingProvider>
      } />
      <Route path="/reading" element={
        <ReadingProvider><StellaReadingDashboard /></ReadingProvider>
      } />
      
      <Route path="/settings" element={<ProfileManagement />} />
      
      {/* Temporary route for viewing mockups */}
      <Route path="/project-mock" element={<ProjectSetupFlow />} />
      <Route path="/mockup/college-strategy-v2" element={<CollegeStrategyMockup2 />} />

      <Route path="/mockup/essays-area" element={<EssayAreaMocks />} />
      <Route path="/mockup/assessment-analysis" element={<AssessmentAnalysisRefined />} />
      <Route path="/mockup/hub" element={<CollegeJourneyRefined />} />
      <Route path="/mockup/essay-module-1" element={<EssayIdeasHybrid />} />
      <Route path="/mockup/essay-module-2" element={<EssayStartProgressive />} />
      <Route path="/mockup/essay-module-3" element={<EssayResultsDisplay />} />
      <Route path="/mockup/essay-module-4" element={<EssayTopicDevelopment />} />
      <Route path="/mockup/essay-module-5" element={<EssayTopicsRedesign />} />
    

      {/* Beckett's Summer Programs Research Hub */}
      <Route path="/beckett/*" element={<CollegePrep />} />
      <Route path="/summer-explorer" element={<CollegePrep />} />
      
      {/* College Journey Routes - All handled by CollegeRouteHandler */}
      <Route path="/college" element={
        <CollegeProvider><CollegeRouteHandler /></CollegeProvider>
      } />
      <Route path="/college/dashboard" element={
        <CollegeProvider><CollegeRouteHandler /></CollegeProvider>
      } />
      <Route path="/college/profile" element={
        <CollegeProvider><CollegeRouteHandler /></CollegeProvider>
      } />
      <Route path="/college/colleges" element={
        <CollegeProvider><CollegeRouteHandler /></CollegeProvider>
      } />
      <Route path="/college/essays" element={
        <CollegeProvider><CollegeRouteHandler /></CollegeProvider>
      } />
      <Route path="/college/timeline" element={
        <CollegeProvider><CollegeRouteHandler /></CollegeProvider>
      } />
      <Route path="/college/timeline-organized" element={
        <CollegeProvider><OrganizedTimeline /></CollegeProvider>
      } />
      <Route path="/college/timeline-workflow" element={
        <CollegeProvider><WorkflowTimelinePage /></CollegeProvider>
      } />
      <Route path="/college/timeline-dev" element={
        <CollegeProvider><TimelineDevPage /></CollegeProvider>
      } />
      <Route path="/college/timeline-unified" element={
        <CollegeProvider><UnifiedTimelineIntegrated /></CollegeProvider>
      } />
      <Route path="/college/financial-aid" element={
        <CollegeProvider><CollegeRouteHandler /></CollegeProvider>
      } />
      <Route path="/college/compare" element={
        <CollegeProvider><CollegeRouteHandler /></CollegeProvider>
      } />
      <Route path="/college/agents/*" element={
        <CollegeProvider><AgentsDashboard /></CollegeProvider>
      } />
      <Route path="/college/hub-v2" element={
        <CollegeProvider><CollegeJourneyHubV2 /></CollegeProvider>
      } />



      {/* Archived 2025-01: EnhancedCollegeDataEntry route
      <Route path="/college/enhanced-data-entry" element={
        <CollegeProvider><CollegeJourneyWrapper><EnhancedCollegeDataEntry /></CollegeJourneyWrapper></CollegeProvider>
      } /> */}
      <Route path="/college/enhanced-data-hub" element={
        <CollegeProvider><CollegeJourneyWrapper><EnhancedCollegeDataHub /></CollegeJourneyWrapper></CollegeProvider>
      } />
      {/* Archived 2025-01: CollegeJourneyHubEnhanced route
      <Route path="/college/enhanced" element={
        <CollegeProvider><CollegeJourneyWrapper><CollegeJourneyHubEnhanced /></CollegeJourneyWrapper></CollegeProvider>
      } /> */}
      <Route path="/college/profile/:collegeId" element={
        <CollegeProvider><CollegeJourneyWrapper><CollegeProfile /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/parent-testing" element={
        isActualParent ? (
          <ParentTestingHub />
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      <Route path="/college/test-profile-wizard" element={
        <CollegeProvider><TestProfileWizard /></CollegeProvider>
      } />
      <Route path="/college/assessment" element={
        <CollegeProvider><CollegeJourneyWrapper><AssessmentEntry /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/strategic-assessment" element={
        <CollegeProvider><CollegeJourneyWrapper><ConversationalAssessment /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/strategic-assessment-results" element={
        <CollegeProvider><CollegeJourneyWrapper><ConversationalResults /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/strategic-profile/:id" element={
        <CollegeProvider><CollegeJourneyWrapper><SavedStrategicProfile /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/assessment-analysis" element={
        <CollegeProvider><CollegeJourneyWrapper><AssessmentAnalysisPage /></CollegeJourneyWrapper></CollegeProvider>
      } />
      {/* Legacy essay routes - now integrated into main /college/essays via CollegeJourneyPage */}
      {/* Kept for backwards compatibility - redirects to main essays tab */}
      <Route path="/college/essay-hub" element={
        <CollegeProvider><CollegeJourneyWrapper><UnifiedEssayHub /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/essay-development" element={
        <CollegeProvider><CollegeJourneyWrapper><UnifiedEssayHub /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/essays/refinement" element={
        <CollegeProvider><CollegeJourneyWrapper><EssayRefinementInterface /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/college-comparison" element={
        <CollegeProvider><CollegeJourneyWrapper><CollegeComparison /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/recommendations" element={
        <CollegeProvider><CollegeJourneyWrapper><CollegeRecommendations /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/recommendations/follow-up-note" element={
        <CollegeProvider><CollegeJourneyWrapper><FollowUpNotePage /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/brag-sheet" element={
        <CollegeProvider><CollegeJourneyWrapper><BragSheetFramework /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/mockup/brag-sheet-enhanced" element={<EnhancedBragSheetMockup />} />

      {/* College Journey Hub Mockups */}
      <Route path="/mockup/college-hub-redesign" element={<CollegeJourneyHubMockup />} />
      <Route path="/mockup/college-hub-scenarios" element={<ContextScenariosDemo />} />
      <Route path="/mockup/college-hub-enhanced" element={<CollegeJourneyHubEnhancedMockup />} />
      <Route path="/mockup/navs" element={<CJWrapperNav />} />
   
      <Route path="/college/academic-profile" element={
        <CollegeProvider><CollegeJourneyWrapper><ComprehensiveStudentProfile /></CollegeJourneyWrapper></CollegeProvider>
      } />
      {/* Redirect old profile-hub URL to the profile tab for backward compatibility */}
      <Route path="/college/profile-hub" element={
        <Navigate to="/college/profile" replace />
      } />

      {/* Parent-Student Sharing System */}
      <Route path="/college/sharing-demo" element={
        <CollegeProvider><SharingDemo /></CollegeProvider>
      } />
      <Route path="/college/sharing" element={
        <CollegeProvider><StudentSharingPanel /></CollegeProvider>
      } />
      <Route path="/college/sharing-dashboard" element={
        <CollegeProvider><StudentSharingDashboard /></CollegeProvider>
      } />

      {/* Legacy parent portal - redirect to new unified portal */}
      <Route path="/parent-portal" element={
        <Navigate to="/college/parent/overview" replace />
      } />

      {/* New Unified Parent Portal with sub-routes */}
      <Route path="/college/parent/*" element={
        isActualParent ? (
          <CollegeProvider><UnifiedParentPortal /></CollegeProvider>
        ) : (
          <Navigate to="/college/dashboard" />
        )
      } />
     {/* <Route path="/college/migration" element={
        <CollegeProvider><MigrationUtility /></CollegeProvider>
      } /> 
      <Route path="/college/status" element={
        <CollegeProvider><StatusChecker /></CollegeProvider>
      } />

      <Route path="/college/fix-members" element={
        <CollegeProvider><FixFamilyMembers /></CollegeProvider>
      } />
      <Route path="/college/diagnostics" element={
        <CollegeProvider><FamilyDiagnostics /></CollegeProvider>
      } />
*/}
      {/* Individual college strategy page - now under /colleges/ path */}
      <Route path="/college/colleges/:collegeId" element={
        <CollegeProvider><CollegeJourneyWrapper><CollegeStrategyV2 /></CollegeJourneyWrapper></CollegeProvider>
      } />
      {/* Legacy route redirect for backwards compatibility */}
      <Route path="/college/enhanced-strategy/:collegeId" element={
        <CollegeProvider><CollegeJourneyWrapper><CollegeStrategyV2 /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/strategy-v2/:collegeId" element={
        <CollegeProvider><CollegeJourneyWrapper></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/college/strategy/:collegeId" element={
        <CollegeProvider><CollegeJourneyWrapper><CollegeStrategy /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/mockup/college-strategy" element={<CollegeStrategyMockup />} />
      <Route path="/college/:collegeId" element={
        <CollegeProvider><CollegeJourneyWrapper><CollegeRouter /></CollegeJourneyWrapper></CollegeProvider>
      } />
      <Route path="/global-college/:id" element={
        <CollegeProvider><CollegeJourneyWrapper><GlobalCollegeViewWrapper /></CollegeJourneyWrapper></CollegeProvider>
      } />

      <Route path="/college/onboarding" element={
        <CollegeProvider><CollegeJourneyWrapper><StudentOnboardingFlow /></CollegeJourneyWrapper></CollegeProvider>
      } />

      {/* Student-accessible college routes */}
      <Route path="/college/comprehensive-student-profile" element={
        <CollegeProvider><CollegeJourneyWrapper><ComprehensiveStudentProfile /></CollegeJourneyWrapper></CollegeProvider>
      } />
      
      {/* Student Control Panel for timeline preferences */}
      <Route path="/student/control-panel" element={
        <CollegeProvider><StudentControlPanel /></CollegeProvider>
      } />
      
      {/* Student Progress Dashboard */}
      <Route path="/student/progress" element={
        <CollegeProvider><TimelineProgressWidget /></CollegeProvider>
      } />
      
      {/* Parent-only 5-Step Strategic Assessment Routes */}
      <Route path="/college/parent-observations" element={
        isActualParent ? (
          <CollegeProvider><ParentObservations /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      <Route path="/college/parent-observations-b" element={
        isActualParent ? (
          <CollegeProvider><ParentObservationsB /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      <Route path="/college/student-context-generator" element={
        isActualParent ? (
          <CollegeProvider><StudentContextGenerator /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      <Route path="/college/question-set-generator" element={
        isActualParent ? (
          <CollegeProvider><QuestionSetGenerator /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      <Route path="/college/dynamic-guidance-generator" element={
        isActualParent ? (
          <CollegeProvider><DynamicGuidanceGenerator /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      <Route path="/college/synthesis-instructions" element={
        isActualParent ? (
          <CollegeProvider><SynthesisInstructionsGenerator /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      <Route path="/college/formatting-customizer" element={
        isActualParent ? (
          <CollegeProvider><FormattingCustomizer /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      {/* Archived 2025-01: UnifiedCollegeParentHub route
      <Route path="/college/family-hub" element={
        isActualParent ? (
          <CollegeProvider><UnifiedCollegeParentHub /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } /> */}

      {/* Parent-only Assessment Results */}
      <Route path="/college/parent-assessment-results" element={
        isActualParent ? (
          <CollegeProvider><ParentAssessmentResults /></CollegeProvider>
        ) : (
          <Navigate to="/college/academic-profile" />
        )
      } />
      
      {/* Claude API Logs */}
      <Route path="/college/claude-api-logs" element={
        <CollegeProvider><ClaudeApiLogs /></CollegeProvider>
      } />

      {/* AI Intel - available to all users */}
      <Route path="/ai-intelligence" element={
        <MalcolmProvider><AIIntelligenceFeed /></MalcolmProvider>
      } />
      
      {/* Intelligence Dashboard - Contextual AI Dashboard */}
      <Route path="/intelligence-dashboard" element={<IntelligenceDashboardV2 />} />
      <Route path="/intelligence-dashboard-v1" element={<IntelligenceDashboard />} />
      <Route path="/intelligence-analytics" element={<AdvancedAnalyticsDashboard />} />
      
      {/* Catch-all fallback route */}
      <Route path="*" element={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-stone-800 mb-4">Welcome to Paper Sedge</h1>
            <p className="text-stone-600 mb-6">Redirecting you to your dashboard...</p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        </div>
      } />
    </Routes>
    </SidebarLayout>
  );
}

// Use a global flag to prevent double initialization in StrictMode
let appInitialized = false;

function App() {
  // Check if splash was already shown in this session
  const [showSplash, setShowSplash] = useState(() => {
    // If we're in PWA mode and already saw splash this session, don't show again
    const hasSeenSplashThisSession = sessionStorage.getItem('splash-seen');
    if (pwaAuthFix.isPWAMode() && hasSeenSplashThisSession) {
      return false;
    }
    // Also check if app was already initialized (handles StrictMode double mount)
    if (appInitialized) {
      return false;
    }
    return true;
  });
  const [appReady, setAppReady] = useState(false);

  // Debug logging for PWA issues
  useEffect(() => {
    console.log('🚀 App Starting:', {
      isPWA: pwaAuthFix.isPWAMode(),
      userAgent: navigator.userAgent,
      displayMode: window.matchMedia('(display-mode: standalone)').matches,
      environment: import.meta.env.MODE
    });
    
    // Check environment variables
    console.log('🔧 Environment Check:', {
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL ? 'Set' : 'Missing',
      supabaseKey: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'Set' : 'Missing'
    });
  }, []);

  // Show splash screen logic - run only once on mount
  useEffect(() => {
    // Mark app as initialized to prevent double init in StrictMode
    appInitialized = true;
    
    const isPWA = pwaAuthFix.isPWAMode();
    const hasSeenSplash = sessionStorage.getItem('splash-seen');
    const isFirstLoad = !localStorage.getItem('app-visited');
    
    console.log('🎬 Splash Logic:', { isPWA, hasSeenSplash, isFirstLoad, appInitialized });
    
    // Always show splash on first visit or PWA launch (unless already shown)
    if ((isFirstLoad || isPWA) && !hasSeenSplash) {
      setShowSplash(true);
      if (isFirstLoad) {
        localStorage.setItem('app-visited', 'true');
      }
    } else if (!isPWA && hasSeenSplash) {
      // Skip splash for regular web visits after first time
      setShowSplash(false);
      setAppReady(true);
    } else if (isPWA && hasSeenSplash) {
      // PWA mode but already saw splash this session
      setShowSplash(false);
      setAppReady(true);
    } else {
      // Default case - show splash but ensure app becomes ready
      setShowSplash(true);
    }
    
    // Fallback: Always ensure app becomes ready after a short delay
    const fallbackTimer = setTimeout(() => {
      console.log('🔧 Fallback: Setting app ready after timeout');
      setShowSplash(false);
      setAppReady(true);
    }, 3000); // 3 second fallback
    
    return () => clearTimeout(fallbackTimer);
  }, []); // Empty dependency array - run only once

  const handleSplashComplete = () => {
    setShowSplash(false);
    setAppReady(true);
    sessionStorage.setItem('splash-seen', 'true');
  };

  // Debug logging for app state
  console.log('🔍 App State:', { showSplash, appReady });

  if (showSplash) {
    return <SplashScreen key="splash-screen" onComplete={handleSplashComplete} />;
  }

  if (!appReady) {
    console.log('⏳ App not ready, showing loading screen');
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Initializing app...</p>
        </div>
      </div>
    );
  }

  console.log('🚀 App rendering main component - v2024.06.19');
  
  return (
    <ErrorBoundary>
      <Router>
        <FamilyProvider>
          <SessionProvider>
            <SharingProvider>
              <div className="min-h-screen bg-stone-50">
                <SprintTimerOverlay />
                <PWAStatus />
                <TestingModeIndicator />
                <UpdateNotification />
                <AppRoutes />
                <InstallPrompt />
              </div>
            </SharingProvider>
          </SessionProvider>
        </FamilyProvider>
      </Router>
    </ErrorBoundary>
  );
}


export default App;
